/*  EmbedNode — Tiptap custom node for arbitrary embedded HTML.
    ─────────────────────────────────────────────────────────────
    Course authors paste iframes (YouTube, Vimeo, CodePen, Google Maps,
    Loom, etc.), Twitter widgets, or any custom HTML snippet. StarterKit
    strips unknown tags, so we store the raw HTML in a base64 attribute
    on an atomic block node. The learner-side renderer decodes it back.

    Editor UX: rendered as a compact "Embed" placeholder card with a
    remove affordance so authors know a snippet is there without seeing
    a live preview inside the editor (avoids third-party scripts
    executing while editing).

    Wire schema:
        <div data-embed-html data-html="BASE64_UTF8" contenteditable="false"></div>
*/
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import React from "react";
import { Code, Trash } from "@phosphor-icons/react";


// UTF-8 safe base64 helpers (some snippets contain non-ASCII chars).
export function encodeEmbed(html) {
    try {
        return btoa(unescape(encodeURIComponent(String(html || ""))));
    } catch { return ""; }
}
export function decodeEmbed(b64) {
    try {
        return decodeURIComponent(escape(atob(String(b64 || ""))));
    } catch { return ""; }
}


function EmbedCard({ node, deleteNode, editor }) {
    const html = decodeEmbed(node.attrs.html);
    const preview = (html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 80);
    const isEditable = editor?.isEditable;
    return (
        <NodeViewWrapper as="div" data-testid="embed-card" contentEditable={false}>
            <div className="my-2 rounded-lg border border-dashed border-primary/40 bg-primary/5 px-3 py-2 flex items-start gap-2">
                <Code size={16} weight="duotone" className="text-primary shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-primary">Custom HTML / Embed</div>
                    <div className="text-xs text-muted-foreground truncate">
                        {preview || <span className="italic">Empty snippet</span>}
                    </div>
                </div>
                {isEditable && (
                    <button
                        type="button"
                        onClick={deleteNode}
                        title="Remove embed"
                        className="w-6 h-6 grid place-items-center rounded border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 transition-colors shrink-0"
                        data-testid="embed-card-remove"
                    >
                        <Trash size={10} weight="bold" />
                    </button>
                )}
            </div>
        </NodeViewWrapper>
    );
}


export const EmbedNode = Node.create({
    name: "embed",
    group: "block",
    atom: true,
    selectable: true,
    draggable: false,

    addAttributes() {
        return { html: { default: "" } };
    },

    parseHTML() {
        return [
            {
                tag: "div[data-embed-html]",
                getAttrs: (el) => ({ html: el.getAttribute("data-html") || "" }),
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            "div",
            mergeAttributes(HTMLAttributes, {
                "data-embed-html": "",
                "data-html": HTMLAttributes.html || "",
                contenteditable: "false",
            }),
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(EmbedCard);
    },
});
